import {
  createAction,
  createActionGroup,
  emptyProps,
  props,
} from '@ngrx/store';
import { Review } from '@app/quality-assurance/models/responses/review';
import { CreateClaimReviewRequest } from '@app/quality-assurance/models/requests/create-claim-review-request';
import { ClaimReview } from '@app/quality-assurance/models/responses/claim-review';
import { FindingSummary } from '@app/quality-assurance/models/responses/finding-summary';
import { CreateOrUpdateFindingRequest } from '@app/quality-assurance/models/requests/create-or-update-finding-request';
import { FindingCreationResponse } from '@app/quality-assurance/models/responses/finding-creation-response';
import {
  GenericTableActionsClass,
  GenericTableActionsWithoutPropClass,
} from '@app/shared/table/helpers/generic-table-actions';
import { QualityAssuranceState } from '@app/quality-assurance/state/quality-assurance.state';
import { QualityAssuranceTablesEnum } from '@app/quality-assurance/models/enums/quality-assurance-tables.enum';
import { getQualityAssuranceFeatureState } from '@app/quality-assurance/state/quality-assurance.selectors';
import { AllFindingsResponse } from '@app/quality-assurance/models/responses/all-findings-response';
import { FindingMetadata } from '@app/quality-assurance/models/responses/finding-metadata';
import { AllProjectsResponse } from '@app/quality-assurance/models/responses/all-projects-response';
import { ProjectReviewType } from '@app/quality-assurance/models/enums/project-review-type';
import { ReviewTypeTile } from '@app/quality-assurance/models/responses/review-type.tile';
import { CreateProjectRequest } from '@app/quality-assurance/models/requests/create-project-request';
import { ProjectCreationResponse } from '@app/quality-assurance/models/responses/project-creation-response';
import { Finding } from '@app/quality-assurance/models/responses/finding';
import { ReviewFindingRequest } from '@app/quality-assurance/models/requests/review-finding-request';
import { CreateCommentRequest } from '@app/quality-assurance/models/requests/create-comment-request';
import { FindingComment } from '@app/quality-assurance/models/responses/finding-comment';
import { ProjectSummary } from '@app/quality-assurance/models/responses/project-summary';
import { LoadProjectTableRequest } from '@app/quality-assurance/models/requests/load-project-table-request';
import { MarkFindingRoundTableInclusionRequest } from '@app/quality-assurance/models/requests/mark-finding-round-table-inclusion-request';
import { CreateRoundTableDateRequest } from '@app/quality-assurance/models/requests/create-round-table-date-request';
import { LoadRoundTableDateRequest } from '@app/quality-assurance/models/requests/load-round-table-date-request';
import { AgGridRequest } from '@app/shared/models/ag-grid-request';
import { RoundTableReviewFindingRequest } from '@app/quality-assurance/models/requests/round-table-review-finding-request';
import { ReviewFindingEscalationRequest } from '@app/quality-assurance/models/requests/review-finding-escalation-request';
import { ReviewSummary } from '@app/quality-assurance/models/responses/review-summary';
import { CloseProjectRequest } from '@app/quality-assurance/models/requests/close-project-request';
import { GenerateClaimReviewWorkListRequest } from '@app/quality-assurance/models/requests/generate-claim-review-work-list-request';
import { ActionGeneratedClaimsWorkListRequest } from '@app/quality-assurance/models/requests/action-generated-claims-work-list-request';
import { GeneratedClaimWorkList } from '@app/quality-assurance/models/responses/generated-claim-work-list';
import { AssignReviewRequest } from '@app/quality-assurance/models/requests/assign-review-request';
import { User } from '@app/user/models/user';
import { RemedialActionsResponse } from '@app/quality-assurance/models/responses/remedial-actions-response';
import { ActionRemedialActionRequest } from '@app/quality-assurance/models/requests/action-remedial-action-request';
import { AwardKudosRequest } from '@app/quality-assurance/models/requests/award-kudos-request';
import { RemoveRoundTableDate } from '@app/quality-assurance/models/requests/RemoveRoundTableDate';
import { ClassificationRequest } from '@app/quality-assurance/models/requests/classification-request';
import { GeneratedReport } from '@app/fin-ops/models/generated-report';
import { UpdateQaReviewStatusRequest } from '@app/quality-assurance/models/requests/update-qa-review-status-request';
import { UpdateProjectRequest } from '@app/quality-assurance/models/responses/update-project-request';
import { UpdateRoundTableRequest } from '@app/quality-assurance/models/requests/update-round-table-request';
import { UnActionedQaFindingNotifications } from '@app/quality-assurance/models/responses/un-actioned-qa-finding-notifications';
import { ProjectsWithApproachingLockdownNotification } from '@app/quality-assurance/models/responses/projects-with-approaching-lockdown-notification';
import { UpdateReviewNoteRequest } from '@app/quality-assurance/models/requests/update-review-note-request';
import { DashboardClaimFindingsResponse } from '@app/quality-assurance/models/responses/dashboard-claim-findings-response';

export const qaActionPrefix = '[Quality Assurance]';

export const loadReview = createActionGroup({
  source: `${qaActionPrefix} Load Review`,
  events: {
    do: props<{ projectId: number; reviewId: number }>(),
    success: props<{ review: Review }>(),
    failure: props<{ error: string }>(),
  },
});
export const createClaimReview = createActionGroup({
  source: `${qaActionPrefix} Create Claim Review`,
  events: {
    do: props<{ request: CreateClaimReviewRequest }>(),
    success: emptyProps,
    failure: props<{ error: string }>(),
  },
});

export const loadClaimReviews = createActionGroup({
  source: `${qaActionPrefix} Load Claim Reviews`,
  events: {
    do: props<{ claimCode: string }>(),
    success: props<{ reviews: ClaimReview[] }>(),
    failure: props<{ error: string }>(),
  },
});

export const loadFinding = createActionGroup({
  source: `${qaActionPrefix} Load Finding`,
  events: {
    do: props<{ findingId: number }>(),
    success: props<{ finding: Finding }>(),
    failure: props<{ error: string }>(),
  },
});

export const createFinding = createActionGroup({
  source: `${qaActionPrefix} Create Finding`,
  events: {
    do: props<{
      request: CreateOrUpdateFindingRequest;
      successCallBack: (response: FindingCreationResponse) => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps,
    failure: props<{ error: string }>(),
  },
});

export const updateFinding = createActionGroup({
  source: `${qaActionPrefix} Update Finding`,
  events: {
    do: props<{
      request: CreateOrUpdateFindingRequest;
      successCallBack: (isCapturedSuccessfully: boolean) => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps,
    failure: props<{ error: string }>(),
  },
});

export const loadReviewFinding = createActionGroup({
  source: `${qaActionPrefix} Load Review Finding`,
  events: {
    do: props<{ findingId: number }>(),
    success: props<{ finding: FindingSummary; findingId: number }>(),
    failure: props<{ error: string; findingId: number }>(),
    clear: emptyProps,
  },
});

export const clearReviewFindings = createAction(
  `${qaActionPrefix} Clear Review Findings`,
);
export const deleteFinding = createActionGroup({
  source: `${qaActionPrefix} Delete Finding`,
  events: {
    do: props<{
      findingId: number;
      successCallBack: () => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps,
    failure: props<{ error: string }>(),
  },
});
export const allFindingsTableActions = new GenericTableActionsClass<
  AllFindingsResponse,
  { reviewIds: number[] },
  QualityAssuranceState
>(
  qaActionPrefix,
  QualityAssuranceTablesEnum.Findings,
  getQualityAssuranceFeatureState,
);

export const allClaimProjectsTableActions =
  new GenericTableActionsWithoutPropClass<
    AllProjectsResponse,
    QualityAssuranceState
  >(
    qaActionPrefix,
    QualityAssuranceTablesEnum.ClaimProjects,
    getQualityAssuranceFeatureState,
  );
export const loadFindingMetadata = createActionGroup({
  source: `${qaActionPrefix} Load Finding Metadata`,
  events: {
    do: emptyProps,
    success: props<{ metadata: FindingMetadata }>(),
    failure: props<{ error: string }>(),
    clear: emptyProps,
  },
});

export const reviewFinding = createActionGroup({
  source: `${qaActionPrefix} Review Finding`,
  events: {
    do: props<{
      request: ReviewFindingRequest;
    }>(),
    success: emptyProps,
    failure: props<{ error: string }>(),
  },
});
export const loadFindingComments = createActionGroup({
  source: `${qaActionPrefix} Load Finding Comments`,
  events: {
    do: props<{ findingId: number }>(),
    success: props<{ comments: FindingComment[] }>(),
    failure: props<{ error: string }>(),
    clear: emptyProps,
  },
});
export const createComment = createActionGroup({
  source: `${qaActionPrefix} Create Comment`,
  events: {
    do: props<{
      request: CreateCommentRequest;
      successCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const getProjectsByReviewType = createActionGroup({
  source: `${qaActionPrefix} Get Projects by review type`,
  events: {
    do: props<{ request: AgGridRequest<AllProjectsResponse> }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getProjectsByReviewTypeExcel = createActionGroup({
  source: `${qaActionPrefix} Get Projects excel by review type`,
  events: {
    do: props<{ request: AgGridRequest<AllProjectsResponse> }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const reviewTypeSelectionActions = createActionGroup({
  source: `${qaActionPrefix} Review Type Selection`,
  events: {
    do: props<{ reviewType: ProjectReviewType }>(),
  },
});

export const loadReviewTypeTiles = createActionGroup({
  source: `${qaActionPrefix} Load Review Type Tiles`,
  events: {
    do: emptyProps,
    success: props<{ tiles: ReviewTypeTile[] }>(),
    failure: props<{ error: string }>(),
    clear: emptyProps,
  },
});
export const loadProject = createActionGroup({
  source: `${qaActionPrefix} Load Project`,
  events: {
    do: props<{ projectId: number }>(),
    success: props<{ project: ProjectSummary }>(),
    failure: props<{ error: string }>(),
  },
});
export const loadProjectTable = (<T>() =>
  createActionGroup({
    source: `${qaActionPrefix} Load Project Table`,
    events: {
      do: props<{ request: LoadProjectTableRequest<T> }>(),
      success: emptyProps,
      failure: emptyProps,
    },
  }))();
export const loadProjectExcel = (<T>() =>
  createActionGroup({
    source: `${qaActionPrefix} Load Project Table Excel Data`,
    events: {
      do: props<{ request: LoadProjectTableRequest<T> }>(),
      success: emptyProps,
      failure: emptyProps,
    },
  }))();
export const downloadExcelExport = createActionGroup({
  source: `${qaActionPrefix} Download excel report`,
  events: {
    do: props<{ report: GeneratedReport }>(),
    success: emptyProps,
    failure: emptyProps,
  },
});
export const markFindingRoundTableInclusion = createActionGroup({
  source: `${qaActionPrefix} Mark Finding Round Table Inclusion`,
  events: {
    do: props<{
      request: MarkFindingRoundTableInclusionRequest;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});
export const roundTableReviewFinding = createActionGroup({
  source: `${qaActionPrefix} Round Table Review Finding`,
  events: {
    do: props<{
      request: RoundTableReviewFindingRequest;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const reviewFindingEscalation = createActionGroup({
  source: `${qaActionPrefix} Review Finding Escalation`,
  events: {
    do: props<{
      request: ReviewFindingEscalationRequest;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});
export const loadReviewSummary = createActionGroup({
  source: `${qaActionPrefix} Load Review Summary`,
  events: {
    do: props<{ projectId: number; reviewId: number }>(),
    success: props<{ review: ReviewSummary }>(),
    failure: props<{ error: string }>(),
  },
});

export const createProject = createActionGroup({
  source: `${qaActionPrefix} Create Project`,
  events: {
    do: props<{
      request: CreateProjectRequest;
      successCallBack: (response: ProjectCreationResponse) => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps,
    failure: props<{ error: string }>(),
  },
});

export const createRoundTableDate = createActionGroup({
  source: `${qaActionPrefix} Create Round Table Date`,
  events: {
    do: props<{
      request: CreateRoundTableDateRequest;
      successCallBack: () => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const loadProjectRoundTableDates = createActionGroup({
  source: `${qaActionPrefix} Get round table dates by project`,
  events: {
    do: props<{ request: LoadRoundTableDateRequest }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const closeProject = createActionGroup({
  source: `${qaActionPrefix} Close Monthly Review`,
  events: {
    do: props<{
      request: CloseProjectRequest;
      successCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});
export const generateClaimsWorkList = createActionGroup({
  source: `${qaActionPrefix} Generate Claims Work List`,
  events: {
    do: props<{
      request: GenerateClaimReviewWorkListRequest;
      successCallBack: (response: GeneratedClaimWorkList) => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});
export const actionGeneratedClaimsWorkList = createActionGroup({
  source: `${qaActionPrefix} Action generated Claims Work List`,
  events: {
    do: props<{
      request: ActionGeneratedClaimsWorkListRequest;
      successCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const getDashboardClaimFindings = createActionGroup({
  source: `${qaActionPrefix} Get dashboard claim findings`,
  events: {
    do: props<{ request: AgGridRequest<DashboardClaimFindingsResponse> }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});
export const getDashboardClaimFindingsExport = createActionGroup({
  source: `${qaActionPrefix} Get dashboard claim findings export`,
  events: {
    do: props<{ request: AgGridRequest<DashboardClaimFindingsResponse> }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});
export const setShowOnlyMyFindings = createActionGroup({
  source: `${qaActionPrefix} Set show only my Qa findings`,
  events: {
    do: props<{ isChecked: boolean }>(),
  },
});
export const assignReview = createActionGroup({
  source: `${qaActionPrefix} Assign Review`,
  events: {
    do: props<{
      request: AssignReviewRequest;
      successCallBack: () => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});
export const loadClaimReviewers = createActionGroup({
  source: `${qaActionPrefix} Load Claim Reviewers`,
  events: {
    do: emptyProps,
    success: props<{ reviewers: User[] }>(),
    failure: props<{ error: string }>(),
    clear: emptyProps,
  },
});

export const getRemedialActions = createActionGroup({
  source: `${qaActionPrefix} Get Action Tracker `,
  events: {
    do: props<{ request: AgGridRequest<RemedialActionsResponse> }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getRemedialActionsExport = createActionGroup({
  source: `${qaActionPrefix} Get Action Tracker export`,
  events: {
    do: props<{ request: AgGridRequest<RemedialActionsResponse> }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});
export const actionRemedialAction = createActionGroup({
  source: `${qaActionPrefix} Action Remedial Action`,
  events: {
    do: props<{
      request: ActionRemedialActionRequest;
      successCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});
export const loadFindingTypeTiles = createActionGroup({
  source: `${qaActionPrefix} Load Finding Type Tiles`,
  events: {
    do: emptyProps,
    success: props<{ tiles: ReviewTypeTile[] }>(),
    failure: props<{ error: string }>(),
    clear: emptyProps,
  },
});

export const loadQaAggregateChart = createActionGroup({
  source: `${qaActionPrefix} Get QA chart data`,
  events: {
    do: props<{ request: ClassificationRequest }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const loadQaRootCauseChart = createActionGroup({
  source: `${qaActionPrefix} Get QA root cause chart data`,
  events: {
    do: props<{ request: ClassificationRequest }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const awardKudos = createActionGroup({
  source: `${qaActionPrefix} Award Kudos`,
  events: {
    do: props<{
      request: AwardKudosRequest;
      successCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const updateRoundTableDate = createActionGroup({
  source: `${qaActionPrefix} Update Round Table Date`,
  events: {
    do: props<{
      request: UpdateRoundTableRequest;
      successCallBack: () => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const removeRoundTableDate = createActionGroup({
  source: `${qaActionPrefix} Remove Round Table Date`,
  events: {
    do: props<{
      request: RemoveRoundTableDate;
      successCallBack: () => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const startQAReview = createActionGroup({
  source: `${qaActionPrefix} Start QA Review`,
  events: {
    do: props<{
      request: UpdateQaReviewStatusRequest;
      successCallBack: () => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const completeQAReview = createActionGroup({
  source: `${qaActionPrefix} Complete QA Review`,
  events: {
    do: props<{
      request: UpdateQaReviewStatusRequest;
      successCallBack: () => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const updateProject = createActionGroup({
  source: `${qaActionPrefix} Update Project`,
  events: {
    do: props<{
      request: UpdateProjectRequest;
      successCallBack: () => void;
      failureCallBack: () => void;
    }>(),
    success: props<{ projectId: number }>(),
    failure: props<{ error: string; projectId: number }>(),
  },
});

export const startQualityAssuranceNotifications = createActionGroup({
  source: `${qaActionPrefix} Start Quality Assurance notification`,
  events: {
    do: emptyProps(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const startUnActionedQaFindingsNotifications = createActionGroup({
  source: `${qaActionPrefix} Create Un Actioned Qa Findings notification`,
  events: {
    do: emptyProps(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const updateUnActionedQaFindingsNotifications = createActionGroup({
  source: `${qaActionPrefix} Update Un Actioned Qa Findings notification`,
  events: {
    do: props<{
      request: UnActionedQaFindingNotifications;
    }>(),
  },
});

export const startProjectsWithApproachingLockdownNotifications =
  createActionGroup({
    source: `${qaActionPrefix} Create projects with approaching lockdown notification`,
    events: {
      do: emptyProps(),
      success: emptyProps(),
      failure: props<{ error: string }>(),
    },
  });
export const updateReviewNote = createActionGroup({
  source: `${qaActionPrefix} Update Review Note`,
  events: {
    do: props<{
      request: UpdateReviewNoteRequest;
      successCallBack: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const updateProjectsWithApproachingLockdownNotifications =
  createActionGroup({
    source: `${qaActionPrefix} Update projects with approaching lockdown notification`,
    events: {
      do: props<{
        request: ProjectsWithApproachingLockdownNotification;
      }>(),
    },
  });
export const deleteProject = createActionGroup({
  source: `${qaActionPrefix} Delete Project`,
  events: {
    do: props<{
      projectId: number;
      successCallBack: () => void;
      failureCallBack: () => void;
    }>(),
    success: emptyProps,
    failure: props<{ error: string }>(),
  },
});
